import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { FocusEvent, SyntheticEvent, useEffect, useRef, useState } from 'react';

const EXPAND_TRANSITION_MS = 320;

const PORTAL_SELECTOR = [
  '[data-radix-popper-content-wrapper]',
  '[data-dropdown-list-container]',
  '[role="dialog"]',
].join(', ');

const hasDraftContent = (container: HTMLElement | null): boolean => {
  const input = container?.querySelector<HTMLElement>(
    'textarea, [contenteditable="true"]'
  );

  if (!input) {
    return false;
  }

  const text =
    input instanceof HTMLTextAreaElement
      ? input.value
      : (input.textContent ?? '');

  return text.trim().length > 0;
};

export interface UseCollapsibleFooterOptions {
  isCollapsible: boolean;
  initiallyFocused: boolean;
}

export const useCollapsibleFooter = ({
  isCollapsible,
  initiallyFocused,
}: UseCollapsibleFooterOptions) => {
  const { params } = useQueryParams();

  const containerRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(initiallyFocused);
  const [hasContent, setHasContent] = useState(false);
  const [isSettled, setIsSettled] = useState(false);

  const isExpanded = !isCollapsible || isHovering || isFocused || hasContent;

  useEffect(() => {
    if (!params.question) {
      return;
    }

    if (!isExpanded) {
      pendingFocusRef.current = true;
    }

    setHasContent(true);
  }, [params.question, isExpanded]);

  useEffect(() => {
    if (!isExpanded) {
      setIsSettled(false);
      return;
    }

    const timeoutId = window.setTimeout(
      () => setIsSettled(true),
      EXPAND_TRANSITION_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded && pendingFocusRef.current) {
      pendingFocusRef.current = false;
      containerRef.current
        ?.querySelector<HTMLElement>('textarea, [contenteditable="true"]')
        ?.focus();
    }
  }, [isExpanded]);

  const containerHandlers = {
    onMouseEnter: () => setIsHovering(true),
    onMouseLeave: () => {
      setIsHovering(false);
      setHasContent(hasDraftContent(containerRef.current));
    },
    onFocusCapture: () => setIsFocused(true),
    onBlurCapture: (event: FocusEvent<HTMLDivElement>) => {
      const nextFocused = event.relatedTarget as Element | null;

      if (
        containerRef.current?.contains(nextFocused) ||
        nextFocused?.closest(PORTAL_SELECTOR)
      ) {
        return;
      }

      setIsFocused(false);
      setHasContent(hasDraftContent(containerRef.current));
    },
    onInputCapture: (event: SyntheticEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement & {
        value?: string;
        textContent?: string | null;
      };
      const text = target.value ?? target.textContent ?? '';
      setHasContent(text.trim().length > 0);
    },
  };

  const triggerHandlers = {
    onClick: () => {
      pendingFocusRef.current = true;
      setIsFocused(true);
    },
    onFocus: () => {
      pendingFocusRef.current = true;
    },
  };

  return {
    containerRef,
    isExpanded,
    isSettled,
    containerHandlers,
    triggerHandlers,
  };
};
